import { useId, useLayoutEffect, useRef, useState, type MouseEvent } from "react";

export interface SparkPoint {
  time: string;
  value: number;
}

interface SparklineProps {
  data?: SparkPoint[];
  height?: number;
  color?: string;
  hoveredIndex?: number | null;
  onHover?: (index: number | null) => void;
}

export default function Sparkline({
  data,
  height = 44,
  color = "#22B06E",
  hoveredIndex,
  onHover,
}: SparklineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const gradientId = useId().replace(/[:]/g, "");

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const points = data || [];
  const n = points.length;
  const max = points.reduce((m, p) => Math.max(m, p.value), 0);
  const pad = 4;
  const innerW = Math.max(1, width - pad * 2);
  const innerH = Math.max(1, height - pad * 2);

  const x = (i: number) => (n <= 1 ? pad + innerW / 2 : pad + (i / (n - 1)) * innerW);
  const y = (v: number) => pad + innerH - (max > 0 ? v / max : 0) * innerH;

  const linePath = points
    .map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(2)},${y(p.value).toFixed(2)}`)
    .join(" ");
  const areaPath =
    n > 0
      ? `${linePath} L${x(n - 1).toFixed(2)},${(pad + innerH).toFixed(2)} L${x(0).toFixed(2)},${(pad + innerH).toFixed(2)} Z`
      : "";

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const relX = (e.clientX - rect.left) / rect.width;
    const index = Math.min(Math.max(Math.round(relX * (n - 1)), 0), n - 1);
    onHover?.(index);
  };

  if (n === 0) {
    return (
      <div ref={containerRef} dir="ltr" style={{ height }} className="w-full flex items-center justify-center">
        <span className="text-[11px] text-text-tertiary">—</span>
      </div>
    );
  }

  const active = hoveredIndex != null && hoveredIndex < n;
  const hx = active ? x(hoveredIndex!) : null;
  const hy = active ? y(points[hoveredIndex!].value) : null;

  return (
    <div
      ref={containerRef}
      dir="ltr"
      style={{ height }}
      className="w-full relative select-none"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => onHover?.(null)}
    >
      {width > 0 && (
        <svg width={width} height={height} className="block overflow-visible">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill={`url(#${gradientId})`} />
          <path
            d={linePath}
            fill="none"
            stroke={color}
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {active && hx != null && hy != null && (
            <g>
              <line
                x1={hx}
                y1={pad}
                x2={hx}
                y2={pad + innerH}
                stroke={color}
                strokeWidth="1"
                strokeDasharray="3 3"
                opacity="0.7"
              />
              <circle cx={hx} cy={hy} r="3.5" fill={color} stroke="#fff" strokeWidth="1.5" />
            </g>
          )}
        </svg>
      )}
    </div>
  );
}
